import ko from 'knockout';
import moment from 'moment';

import { onKeyDown, onInput } from '../../multifield-utils';
import { customFetch as fetch, CustomResponse, CustomError } from '../../fetch';
import { resolveCdnUrl } from '../../cdn-url-resolver';
import { lookupAddress } from '../../postalCodeApi';
import Gender from '../../custom/gender';

declare var model: any;

enum RegistrationSteps {
    Profile = 1,
    AdditionalInformation = 2,
    WorkingAs = 3,
    Finished = 4
}

interface IForm {
    submit(): Promise<CustomResponse>;
}

class RegistrationModel {
    initialized: KnockoutObservable<boolean>;

    currentStep: KnockoutObservable<number>;
    nextStepLoading: KnockoutObservable<boolean>;

    profile: KnockoutObservable<ProfileDetailsModel>;
    additionalInformation: KnockoutObservable<AdditionalInformationModel>;

    validationErrors: KnockoutObservableArray<CustomError>;

    constructor(model: any = {}) {
        this.currentStep = ko.observable<number>(model.step || 1);
        this.nextStepLoading = ko.observable<boolean>(false);
        this.validationErrors = ko.observableArray<CustomError>();
        this.initialized = ko.observable<boolean>(false);

        this.profile = ko.observable<ProfileDetailsModel>(new ProfileDetailsModel(model, this));
        this.additionalInformation = ko.observable<AdditionalInformationModel>(new AdditionalInformationModel(model, this));

        this.initialized(true);
    }

    public previousStep() {
        this.currentStep(this.currentStep() - 1);
    }

    public async nextStep() {
        let currentForm = this.currentForm();
        if (currentForm) {
            this.nextStepLoading(true);
            let result = await currentForm.submit();
            this.nextStepLoading(false);
            if (result.errors) {
                this.validationErrors(result.errors);

                if (result.errors.length > 0) {
                    const timelineElement = document.getElementById('timeline')!;
                    timelineElement.scrollIntoView();
                    return;
                }
            } else {
                if (currentForm instanceof ProfileDetailsModel) {
                    //Reload after registration submission for Identity to reload IsAuthenticated status
                    window.location.reload();
                    return;
                }
            }
        }

        if (this.currentStep() === 2) {
            window.location.href = '/Employee/Home/Index';
        } else {
            this.validationErrors([]);
            let nextStep = this.currentStep() + 1;

            this.currentStep(nextStep);
        }
    }

    public toStep(step: number) {
        if (this.currentStep() > step) {
            this.currentStep(step);
        }
    }

    private currentForm() {
        switch (this.currentStep()) {
            case RegistrationSteps.Profile:
                return this.profile();
            case RegistrationSteps.AdditionalInformation:
                return this.additionalInformation();
        }

        return undefined;
    }
}

class ProfileDetailsModel implements IForm {
    parent: KnockoutObservable<RegistrationModel>;

    userId: KnockoutObservable<string>;
    loginProvider: KnockoutObservable<string>;
    providerKey: KnockoutObservable<string>;

    avatar: KnockoutObservable<any>;
    avatarHash: KnockoutObservable<string>;
    avatarUrl: KnockoutObservable<string>;
    avatarSrc: KnockoutComputed<any>;

    username: KnockoutObservable<string>;
    password: KnockoutObservable<string>;
    confirmPassword: KnockoutObservable<string>;

    firstName: KnockoutObservable<string>;
    lastName: KnockoutObservable<string>;
    gender: KnockoutObservable<Gender>;
    birthdayDay: KnockoutObservable<string>;
    birthdayMonth: KnockoutObservable<string>;
    birthdayYear: KnockoutObservable<string>;
    birthday: KnockoutComputed<moment.Moment | null>;
    address: KnockoutObservable<string>;
    houseNumber: KnockoutObservable<string>;
    postalCode: KnockoutObservable<string>;
    city: KnockoutObservable<string>;
    phoneNumber: KnockoutObservable<string>;
    acceptedTermsAndConditions: KnockoutObservable<boolean>;
    referral: KnockoutObservable<number>;

    constructor(model: any = {}, parent: RegistrationModel) {
        this.parent = ko.observable<RegistrationModel>(parent);

        this.avatar = ko.observable<any>({
            dataURL: ko.observable<any>(),
            file: ko.observable<any>()
        }).extend({
            errorHandler: {
                source: parent.validationErrors, id: 'Avatar'
            }
        });

        this.avatarHash = ko.observable(model.avatarHash || '');
        this.avatarUrl = ko.observable(model.avatarUrl);

        this.avatar().dataURL.subscribe(() => {
            this.avatarHash('');
            this.avatarUrl('');
        });

        if (this.avatarUrl() || this.avatarHash()) {
            this.avatar().file = ko.observable({ name: model.avatarFileName });
        }

        this.avatarSrc = ko.pureComputed(() => {
            if (this.avatar().dataURL()) {
                return this.avatar().dataURL();
            }

            if (this.avatarHash()) {
                return resolveCdnUrl(this.avatarHash());
            }

            if (this.avatarUrl()) {
                return this.avatarUrl();
            }

            return '';
        });

        this.userId = ko.observable<string>(model.userId || '');
        this.loginProvider = ko.observable<string>(model.loginProvider || '');
        this.providerKey = ko.observable<string>(model.providerKey || '');

        this.username = ko.observable<string>(model.userName || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'UserName' }
            });
        this.password = ko.observable<string>('')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'Password' }
            });
        this.confirmPassword = ko.observable<string>('')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'ConfirmPassword' }
            });
        this.firstName = ko.observable<string>(model.firstName || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'FirstName' }
            });
        this.lastName = ko.observable<string>(model.lastName || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'LastName' }
            });
        this.gender = ko.observable<Gender>(model.gender || Gender.Male)
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'Gender' }
            });
        const birthday = model.birthday ? moment(model.birthday) : null;
        this.birthdayDay = ko.observable<string>(birthday ? birthday.format('DD') : '')
            .extend({
                range: { min: 0, max: 31 }
            });
        this.birthdayMonth = ko.observable<string>(birthday ? birthday.format('MM') : '')
            .extend({
                range: { min: 0, max: 12 }
            });
        this.birthdayYear = ko.observable<string>(birthday ? birthday.format('YYYY') : '')
            .extend({
                range: { min: 0, max: moment().year() }
            });
        this.birthday = ko.pureComputed(() => {
            if (this.birthdayDay() != null
                && this.birthdayMonth() != null
                && this.birthdayYear() != null) {
                return moment(`${this.birthdayYear()}-${this.birthdayMonth()}-${this.birthdayDay()}`, 'YYYY-M-D');
            }

            return null;
        }).extend({
            errorHandler: { source: parent.validationErrors, id: 'Birthday' }
        });

        this.address = ko.observable<string>(model.address || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'Address' }
            });
        this.postalCode = ko.observable<string>(model.postalCode || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'PostalCode' }
            });
        this.houseNumber = ko.observable<string>(model.houseNumber || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'HouseNumber' }
            });
        this.city = ko.observable<string>(model.city || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'City' }
            });
        this.phoneNumber = ko.observable<string>(model.phoneNumber || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'PhoneNumber' }
            });
        this.acceptedTermsAndConditions = ko.observable<boolean>(false)
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'AcceptedTermsConditions' }
            });

        this.referral = ko.observable<number>(model.referral);
    }

    clearAvatar(): void {
        if (typeof (this.avatar().clear) === 'function') {
            this.avatar().clear();
        }
    }

    async submit(): Promise<CustomResponse> {
        let fd = new FormData();
        fd.append('avatar', this.avatar().file());
        fd.append('avatarHash', this.avatarHash());
        fd.append('userName', this.username());
        fd.append('password', this.password());
        fd.append('confirmPassword', this.confirmPassword());
        fd.append('firstName', this.firstName());
        fd.append('lastName', this.lastName());
        fd.append('gender', this.gender());
        const birthday = this.birthday();
        fd.append('birthday', birthday ? birthday.format() : '');
        fd.append('address', this.address());
        fd.append('houseNumber', this.houseNumber());
        fd.append('postalCode', this.postalCode());
        fd.append('city', this.city());
        fd.append('phoneNumber', this.phoneNumber());
        fd.append('acceptedTermsConditions', this.acceptedTermsAndConditions().toString());
        fd.append('providerKey', this.providerKey());
        fd.append('referral', this.referral().toString());

        const url = this.userId()
            ? '/Account/EditRegistrationEmployee'
            : '/Account/RegisterEmployee';

        const response = await fetch(url, {
            body: fd,
            method: 'POST'
        });

        if (!response.errors
            || response.errors.length === 0) {
            this.userId(response.data.userId);
        }
        return response;
    }

    onKeyDown = onKeyDown;
    onInput = onInput;

    async onLeavePostalCodeOrHouseNumberField() {
        if (this.postalCode() && this.postalCode().length > 0
            && this.houseNumber() && this.houseNumber().length > 0) {
            const result = await lookupAddress(this.postalCode(), this.houseNumber());

            if (result.success) {
                this.address(result.street);
                this.city(result.town);
            }
        }
    }
}

class AdditionalInformationModel implements IForm {
    parent: KnockoutObservable<RegistrationModel>;

    personalDescription: KnockoutObservable<string>;
    yearsWorkExperience: KnockoutObservable<number>;
    hasDrivingLicense: KnockoutObservable<boolean>;
    hasCar: KnockoutObservable<boolean>;

    constructor(model: any = {}, parent: RegistrationModel) {
        this.parent = ko.observable<RegistrationModel>(parent);

        this.personalDescription = ko.observable<string>(model.personalDescription || '')
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'PersonalDescription' }
            });
        this.yearsWorkExperience = ko.observable<number>(model.yearsWorkExperience || 0)
            .extend({
                errorHandler: { source: parent.validationErrors, id: 'YearsWorkExperience' }
            });
        this.hasDrivingLicense = ko.observable<boolean>(false);
        this.hasCar = ko.observable<boolean>(false);
    }

    async submit(): Promise<CustomResponse> {
        let fd = new FormData();
        fd.append('personalDescription', this.personalDescription());
        fd.append('yearsWorkExperience', this.yearsWorkExperience().toString());
        fd.append('hasDrivingLicense',  this.hasDrivingLicense().toString());
        fd.append('hasCar', this.hasCar().toString());

        return await fetch('/Employee/Employee/SetAdditionalProfileInformation', {
            method: 'POST',
            body: fd
        });
    }
}

ko.options.useOnlyNativeEvents = true;
(<any>ko).fileBindings.defaultOptions.buttonText = 'Kies bestand...';
(<any>ko).fileBindings.defaultOptions.noFileText = '';

const pageModel = new RegistrationModel(model || {});
ko.applyBindings(pageModel);
